import { type ReactNode } from 'react';
import { View } from 'react-native';

import { CheckCircle } from '@/components/ui/CheckCircle';
import { cn } from '@/components/ui/cn';
import { Text } from '@/components/ui/Text';

import { AfterSealBadge } from './AfterSealBadge';

export interface SealedStepRowProps {
  label: string;
  checked: boolean;
  /** Attribution name shown when checked, e.g. "Jane". */
  by?: string | null;
  /** Clock stamp shown when checked, e.g. "15:21". */
  timeLabel?: string | null;
  /** The check reached the server after the run was sealed — see `AfterSealBadge`. */
  late?: boolean;
  /** First row hides the rail segment above its node. */
  isFirst?: boolean;
  /** Last row hides the rail segment below its node. */
  isLast?: boolean;
  /** Notes / reference note rendered under the step. */
  children?: ReactNode;
}

/**
 * A read-only step in a sealed run, drawn as a timeline row: a connecting rail
 * with a node marker on the left and the step label + attribution on the right.
 * Emphasis is inverted from the active list — done steps are bold, not-done
 * steps are faded and tagged "NOT DONE".
 */
export function SealedStepRow({
  label,
  checked,
  by,
  timeLabel,
  late,
  isFirst,
  isLast,
  children,
}: SealedStepRowProps) {
  return (
    // No cross-axis alignment override: the rail column stretches to the full
    // row height so its bottom rail segment reaches the next row's node.
    <View className="flex-row">
      {/* Rail column — node center sits 20px from the top (pt-2 + half of the
          24px node), which the two absolute line segments meet at. */}
      <View className="relative w-9 items-center">
        {!isFirst ? (
          <View className="absolute left-1/2 top-0 -ml-px h-5 w-0.5 bg-border" />
        ) : null}
        {!isLast ? (
          <View className="absolute bottom-0 left-1/2 top-5 -ml-px w-0.5 bg-border" />
        ) : null}
        <View className="pt-2">
          <CheckCircle checked={checked} dashed={!checked} />
        </View>
      </View>

      <View className="flex-1 gap-2 pb-4 pt-2">
        <View className="flex-row items-start gap-3">
          <Text
            variant="none"
            numberOfLines={2}
            className={cn(
              'flex-1 text-base leading-normal',
              checked
                ? 'font-sans-semibold text-foreground'
                : 'font-sans-medium text-faint',
            )}
          >
            {label}
          </Text>
          {checked ? (
            <View className="items-end gap-1">
              <Text variant="accentSmall" numberOfLines={1} className="pt-0.5">
                {timeLabel}
                {by ? ` · ${by}` : ''}
              </Text>
              {late ? <AfterSealBadge /> : null}
            </View>
          ) : (
            <Text
              variant="none"
              className="pt-0.5 font-accent text-xs uppercase tracking-[0.12em] text-faint"
            >
              Not done
            </Text>
          )}
        </View>
        {children}
      </View>
    </View>
  );
}
